<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';

const props = defineProps({
  audienceList: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits([
  'import',
  'importRegistry',
  'linkCandidates',
  'viewMembers',
  'createCampaign',
  'edit',
  'delete',
]);

const { t } = useI18n();

const statusStyle = computed(() => {
  const map = {
    importing: 'bg-n-amber-3 text-n-amber-11',
    error: 'bg-n-ruby-3 text-n-ruby-11',
    ready: 'bg-n-teal-3 text-n-teal-11',
  };
  return map[props.audienceList.status] ?? map.ready;
});

// Mirrors statusStyle's fallback: an unmapped status used to render the raw
// key ("CAMPAIGN.AUDIENCE.STATUS.FOO") straight onto the card.
const KNOWN_STATUSES = ['ready', 'importing', 'error'];
const translatedStatusLabels = computed(() => ({
  ready: t('CAMPAIGN.AUDIENCE.STATUS.READY'),
  importing: t('CAMPAIGN.AUDIENCE.STATUS.IMPORTING'),
  error: t('CAMPAIGN.AUDIENCE.STATUS.ERROR'),
}));
const statusLabel = computed(() => {
  const status = props.audienceList.status;
  const key = KNOWN_STATUSES.includes(status) ? status : 'ready';
  return translatedStatusLabels.value[key];
});

const stats = computed(() => [
  {
    key: 'contacts',
    label: t('CAMPAIGN.AUDIENCE.CARD.CONTACTS'),
    value: props.audienceList.contacts_count ?? 0,
  },
  {
    key: 'phone',
    label: t('CAMPAIGN.AUDIENCE.CARD.WITH_PHONE'),
    value: props.audienceList.phone_count ?? 0,
  },
  {
    key: 'email',
    label: t('CAMPAIGN.AUDIENCE.CARD.WITH_EMAIL'),
    value: props.audienceList.email_count ?? 0,
  },
  {
    key: 'rows',
    label: t('CAMPAIGN.AUDIENCE.CARD.ROWS'),
    value: props.audienceList.members_count ?? 0,
  },
]);

const lastImportedLabel = computed(() => {
  if (!props.audienceList.last_imported_at) {
    return t('CAMPAIGN.AUDIENCE.CARD.NEVER_IMPORTED');
  }
  const date = new Date(props.audienceList.last_imported_at * 1000);
  return t('CAMPAIGN.AUDIENCE.CARD.LAST_IMPORT', {
    date: date.toLocaleString(),
  });
});

const isEmpty = computed(() => (props.audienceList.contacts_count ?? 0) === 0);

const latestImport = computed(() => props.audienceList.latest_import ?? null);

// One line that answers "did my last upload work?" without opening anything.
const latestImportSummary = computed(() => {
  const record = latestImport.value;
  if (!record) return '';
  if (record.status === 'failed') {
    return record.error_message || t('CAMPAIGN.AUDIENCE.CARD.IMPORT_FAILED');
  }
  if (record.status !== 'completed') return '';

  return t('CAMPAIGN.AUDIENCE.CARD.LAST_IMPORT_SUMMARY', {
    imported: record.imported_count ?? 0,
    updated: record.updated_count ?? 0,
    rejected: (record.skipped_count ?? 0) + (record.failed_count ?? 0),
  });
});

const latestImportFailed = computed(
  () => latestImport.value?.status === 'failed'
);
</script>

<template>
  <CardLayout>
    <div class="flex flex-col w-full gap-4">
      <div class="flex items-start justify-between w-full gap-3">
        <div class="flex flex-col gap-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-base font-medium truncate text-n-slate-12">
              {{ audienceList.name }}
            </span>
            <span
              class="px-2 py-0.5 text-xs font-medium rounded-md shrink-0"
              :class="statusStyle"
            >
              {{ statusLabel }}
            </span>
          </div>
          <p
            v-if="audienceList.description"
            class="text-sm truncate text-n-slate-11"
          >
            {{ audienceList.description }}
          </p>
          <p class="text-xs text-n-slate-10">{{ lastImportedLabel }}</p>
          <p
            v-if="latestImportSummary"
            class="text-xs"
            :class="latestImportFailed ? 'text-n-ruby-11' : 'text-n-slate-11'"
          >
            {{ latestImportSummary }}
          </p>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <Button
            variant="faded"
            color="slate"
            size="sm"
            icon="i-lucide-pencil"
            :aria-label="t('CAMPAIGN.AUDIENCE.ACTIONS.EDIT')"
            @click="emit('edit', audienceList)"
          />
          <Button
            variant="faded"
            color="ruby"
            size="sm"
            icon="i-lucide-trash-2"
            :aria-label="t('CAMPAIGN.AUDIENCE.ACTIONS.DELETE')"
            @click="emit('delete', audienceList)"
          />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div
          v-for="stat in stats"
          :key="stat.key"
          class="flex flex-col gap-0.5 px-3 py-2 rounded-lg bg-n-alpha-1"
        >
          <span class="text-lg font-medium text-n-slate-12">
            {{ stat.value }}
          </span>
          <span class="text-xs text-n-slate-11">{{ stat.label }}</span>
        </div>
      </div>

      <div
        v-if="audienceList.custom_fields?.length"
        class="flex flex-wrap items-center gap-1.5"
      >
        <span class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.AUDIENCE.CARD.CUSTOM_FIELDS') }}
        </span>
        <span
          v-for="field in audienceList.custom_fields"
          :key="field"
          class="px-2 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
        >
          {{ field }}
        </span>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-user-round-plus"
          :label="t('CAMPAIGN.AUDIENCE.ACTIONS.LINK_CANDIDATES')"
          @click="emit('linkCandidates', audienceList)"
        />
        <Button
          size="sm"
          icon="i-lucide-upload"
          :label="t('CAMPAIGN.AUDIENCE.ACTIONS.IMPORT')"
          @click="emit('import', audienceList)"
        />
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-database"
          :label="t('CAMPAIGN.AUDIENCE.ACTIONS.IMPORT_REGISTRY')"
          @click="emit('importRegistry', audienceList)"
        />
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-list"
          :label="t('CAMPAIGN.AUDIENCE.ACTIONS.VIEW_MEMBERS')"
          @click="emit('viewMembers', audienceList)"
        />
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-send"
          :label="t('CAMPAIGN.AUDIENCE.ACTIONS.CREATE_CAMPAIGN')"
          :disabled="isEmpty"
          @click="emit('createCampaign', audienceList)"
        />
        <span v-if="isEmpty" class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.AUDIENCE.CARD.IMPORT_FIRST') }}
        </span>
      </div>
    </div>
  </CardLayout>
</template>
